import * as React from 'react'
import * as m from 'motion/react-m'

import { Flex } from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import { PostMiniatureButton } from '~publish/pages/Community/PostMiniature'
import {
  isMediaFirstPost,
  postSupportsComments,
} from '~publish/pages/Community/PostMiniature/utils'
import { isOlderThanRetentionWindow } from '~publish/pages/Community/util/retention'

import { CommentsSummaryBar } from './CommentsSummaryBar'
import { CompactPostNavigationCard } from './CompactPostNavigationCard'
import { useCommentStatus } from '../../../CommunityFilters/useFilters'
import {
  CommentsList,
  CommentsList_Channel,
} from '../../CommentsList/CommentsList'
import type { CommentsListChannel } from '../../CommentsList/CommentsList'
import { UnsupportedPostEmptyState } from '../../CommentsList/EmptyStates'
import {
  isMonthlyCleanupStatus,
  MonthlyCleanupBanner,
} from '../../CommentsList/MonthlyCleanupBanner'
import { useCompactCardOverlay } from '../hooks/useCompactCardOverlay'
import type { PostListItem } from '../types'

import styles from './CommentsForPost.module.css'

import type { JSX } from 'react'

export const CommentsForPost_Channel = graphql(
  `
    fragment CommentsForPost_Channel on Channel @_unmask {
      service
      ...CommentsList_Channel
    }
  `,
  [CommentsList_Channel],
)

/**
 * `CommentsList` still types its channels against a codegen-backed intersection,
 * so the channel flowing through here must satisfy both it and the colocated
 * fragment. The intersection collapses to the fragment type once that consumer
 * migrates (CORE-6384).
 */
export type CommentsForPostChannel = FragmentOf<
  typeof CommentsForPost_Channel
> &
  CommentsListChannel

type CommentsForPostProps = {
  post: FragmentOf<typeof CommentsPostCard_Post>
  /** The selected post's channel — the comments below belong to it. */
  channel: CommentsForPostChannel
  /**
   * Channels the monthly cleanup prompt covers — every channel in view, so the
   * all-channels view gets its channel-picker variant rather than a prompt
   * scoped to whichever post happens to be selected. Defaults to the post's
   * own channel.
   */
  cleanupChannels?: CommentsForPostChannel[]
  posts?: PostListItem[]
  selectedPostId?: string
  onPostSelect?: (postId: string) => void
  isPanelPinned?: boolean
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
  panelToggle?: React.ReactNode
}

export const CommentsForPost = ({
  post,
  channel,
  cleanupChannels,
  posts,
  selectedPostId,
  onPostSelect,
  isPanelPinned,
  scrollContainerRef,
  panelToggle,
}: CommentsForPostProps): JSX.Element => {
  const resolvedPost = readFragment(CommentsPostCard_Post, post)
  const status = useCommentStatus()

  // Reset scroll position when switching posts. The scroll container lives
  // in the parent and persists across post changes (key={selectedPostId}
  // only remounts this component, not the parent scroll container). Without this, the stale
  // scroll position can trick useCompactCardOverlay into thinking the
  // miniature is scrolled out of view, hiding it on the new post.
  React.useEffect(() => {
    if (scrollContainerRef?.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [scrollContainerRef])

  const { miniatureRef, isScrolledPast } = useCompactCardOverlay({
    isPanelPinned,
    scrollContainerRef,
  })

  const monthlyCleanupChannels = cleanupChannels ?? [channel]

  const isMediaFirstVariant = isMediaFirstPost(resolvedPost)
  const supportsComments = postSupportsComments(resolvedPost)
  const shouldFadeMiniature = !isPanelPinned && isScrolledPast
  // `sentAt` is empty on posts Buffer did not send, which is most of what
  // Community ingests. `dueAt` is the date the card shows.
  const isPostOlderThanRetentionWindow = isOlderThanRetentionWindow(
    resolvedPost.dueAt,
  )

  return (
    <Flex
      direction="column"
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This labelled Flex container defines the comments region without changing its layout primitive.
      role="region"
      aria-label="Comments for this post"
      gap="md"
      className={styles.container}
      data-collapsed={!isPanelPinned || undefined}
    >
      {/* Permanent navigation bar — always visible when collapsed */}
      {!isPanelPinned && (
        <CompactPostNavigationCard
          post={post}
          postId={resolvedPost.id}
          posts={posts}
          selectedPostId={selectedPostId}
          onPostSelect={onPostSelect}
          isScrolledPast={isScrolledPast}
          panelToggle={panelToggle}
        />
      )}
      {isMonthlyCleanupStatus(status) && (
        <MonthlyCleanupBanner channels={monthlyCleanupChannels} />
      )}
      <m.div
        ref={miniatureRef}
        className={styles.miniatureWrapper}
        aria-hidden={shouldFadeMiniature}
        animate={{
          opacity: shouldFadeMiniature ? 0 : 1,
          scale: shouldFadeMiniature ? (isMediaFirstVariant ? 0.7 : 0.9) : 1,
          y: shouldFadeMiniature ? (isMediaFirstVariant ? -80 : -20) : 0,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        style={{
          pointerEvents: shouldFadeMiniature ? 'none' : 'auto',
        }}
      >
        <PostMiniatureButton post={post} className={styles.postMiniature} />
      </m.div>
      {isPanelPinned && (
        <CommentsSummaryBar
          postId={resolvedPost.id}
          channelService={channel.service}
          status={status}
          isOlderThanRetentionWindow={isPostOlderThanRetentionWindow}
        />
      )}
      {supportsComments ? (
        <CommentsList channels={[channel]} post={resolvedPost} />
      ) : (
        <div className={styles.emptyStateWrapper}>
          <UnsupportedPostEmptyState />
        </div>
      )}
    </Flex>
  )
}
